import React, { useState, useCallback, useEffect } from 'react';
import { Input, Checkbox, Card, Descriptions, Tag, Typography, Button, message, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { GetProp } from 'antd';
import { DomainKey, FlowNode, FlowEdge, FlowData, ALL_DOMAINS, DOMAIN_COLORS, DOMAIN_LABELS, loadFlowData } from './moduleFlowData';
import ModuleFlowCanvas from './ModuleFlowCanvas';

const { Text } = Typography;

interface EdgeInfo { name: string; tables: string; cross: boolean }
interface SelectedInfo {
  id: string; name: string; domain: DomainKey; status: string;
  writeTables: string; readTables: string;
  outgoing: EdgeInfo[]; incoming: EdgeInfo[];
}

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  done: { color: 'success', label: 'DONE' },
  progress: { color: 'warning', label: 'IN PROGRESS' },
  notstarted: { color: 'default', label: 'NOT STARTED' },
};

const mono = { fontFamily: 'monospace', fontSize: 11, color: '#8be9fd' } as const;
const swatch = (bg: string) => ({ display: 'inline-block' as const, width: 14, height: 14, borderRadius: 3, background: bg, marginRight: 6, verticalAlign: 'middle' as const });

const EdgeList: React.FC<{ items: EdgeInfo[]; arrow: string }> = ({ items, arrow }) =>
  items.length === 0 ? <div style={{ color: '#666', fontSize: 12 }}>None</div> : (
    <>{items.map((e, i) => (
      <div key={i} style={{ color: '#e0e0e0', fontSize: 12, marginTop: 2 }}>
        {arrow} {e.name} <span style={mono}>({e.tables})</span>
        {e.cross && <Tag color="error" style={{ marginLeft: 4, fontSize: 10 }}>CROSS</Tag>}
      </div>
    ))}</>
  );

const ModuleFlowPage: React.FC = () => {
  const navigate = useNavigate();
  const [flowData, setFlowData] = useState<FlowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [domainFilter, setDomainFilter] = useState<Record<DomainKey, boolean>>(() => {
    const init: Record<string, boolean> = {};
    ALL_DOMAINS.forEach(d => { init[d] = true; });
    return init as Record<DomainKey, boolean>;
  });
  const [selected, setSelected] = useState<SelectedInfo | null>(null);

  // Load JSON data on mount
  useEffect(() => {
    loadFlowData()
      .then(data => { setFlowData(data); setLoading(false); })
      .catch(err => { console.error(err); message.error('載入模組關聯資料失敗'); setLoading(false); });
  }, []);

  const nodeMap = React.useMemo(() => {
    if (!flowData) return {};
    const m: Record<string, { name: string; domain: DomainKey }> = {};
    flowData.nodes.forEach(n => { m[n.id] = { name: n.name, domain: n.domain }; });
    return m;
  }, [flowData]);

  const handleNodeSelect = useCallback((node: { id: string; name: string; domain: DomainKey; status: string; writeTables: string; readTables: string } | null) => {
    if (!node || !flowData) { setSelected(null); return; }
    const mapEdges = (arr: FlowEdge[], key: 'from' | 'to', other: 'from' | 'to') =>
      arr.filter(e => e[key] === node.id && domainFilter[nodeMap[e[other]]?.domain])
        .map(e => ({ name: nodeMap[e[other]]?.name || e[other], tables: e.tables, cross: e.crossDomain }));
    setSelected({ ...node, outgoing: mapEdges(flowData.edges, 'from', 'to'), incoming: mapEdges(flowData.edges, 'to', 'from') });
  }, [domainFilter, nodeMap, flowData]);

  const handleNodeDoubleClick = useCallback((node: { id: string; name: string; status: string }) => {
    if (!flowData) return;
    if (node.status !== 'done' && node.status !== 'progress') {
      message.warning(`${node.name}（${node.id}）模組尚未完成，無法開啟`);
      return;
    }
    const route = flowData.routeMap[node.id];
    if (!route) {
      message.info(`${node.name}（${node.id}）尚未建立前端路由`);
      return;
    }
    navigate(route);
  }, [navigate, flowData]);

  const handleDomainChange = useCallback((checked: GetProp<typeof Checkbox.Group, 'value'>) => {
    const next: Record<string, boolean> = {};
    ALL_DOMAINS.forEach(d => { next[d] = checked.includes(d); });
    setDomainFilter(next as Record<DomainKey, boolean>);
  }, []);

  if (loading || !flowData) {
    return (
      <div style={{ display: 'flex', height: '100vh', background: '#1a1a2e', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" tip="載入模組關聯圖..." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#1a1a2e' }}>
      {/* Left: Legend + Filter */}
      <div style={{ width: 220, flexShrink: 0, padding: 12, overflowY: 'auto', background: 'rgba(20,20,40,0.95)', borderRight: '1px solid #444' }}>
        <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/erp')}
          style={{ color: '#aaa', marginBottom: 8, padding: '4px 0' }}>返回 ERP</Button>
        <Text strong style={{ color: '#fff', fontSize: 15, display: 'block' }}>ERP 模組關聯圖</Text>
        <div style={{ color: '#666', fontSize: 11, marginTop: 2 }}>
          {flowData.nodes.length} 模組 · {flowData.edges.length} 關聯
        </div>
        <div style={{ marginTop: 12 }}>
          <Checkbox.Group value={ALL_DOMAINS.filter(d => domainFilter[d])} onChange={handleDomainChange}
            style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {ALL_DOMAINS.map(d => (
              <Checkbox key={d} value={d} style={{ color: '#e0e0e0' }}>
                <span style={swatch(DOMAIN_COLORS[d])} />{DOMAIN_LABELS[d]}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </div>
        <div style={{ marginTop: 16, paddingTop: 10, borderTop: '1px solid #444' }}>
          <Text style={{ color: '#aaa', fontSize: 12 }}>EDGE STYLES</Text>
          <div style={{ marginTop: 4, color: '#e0e0e0', fontSize: 13 }}>
            <div><span style={{ display: 'inline-block', width: 20, borderTop: '2px dashed #e74c3c', marginRight: 6, verticalAlign: 'middle' }} /> Cross-domain</div>
            <div style={{ marginTop: 2 }}><span style={{ display: 'inline-block', width: 20, borderTop: '2px solid #4a9eff', marginRight: 6, verticalAlign: 'middle' }} /> Same domain</div>
          </div>
        </div>
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #444' }}>
          <Text style={{ color: '#aaa', fontSize: 12 }}>NODE STATUS</Text>
          <div style={{ marginTop: 4, color: '#e0e0e0', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div><span style={{ ...swatch('transparent'), border: '2px solid #2ecc71' }} /> Done</div>
            <div><span style={{ ...swatch('transparent'), border: '2px solid #f39c12' }} /> In Progress</div>
            <div><span style={{ ...swatch('transparent'), border: '2px dashed #666' }} /> Not Started</div>
            <div><span style={{ ...swatch('#f1c40f'), borderRadius: '50%' }} /> SC (Hub)</div>
          </div>
        </div>
        <div style={{ marginTop: 16, color: '#888', fontSize: 11 }}>
          Scroll: zoom | Drag: pan<br />
          Drag node: move<br />
          <span style={{ color: '#4a9eff' }}>Double-click: 開啟模組</span>
        </div>
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #444', color: '#555', fontSize: 10 }}>
          資料來源：public/module-flow.json<br />
          修改 JSON 即可調整關聯圖
        </div>
      </div>

      {/* Center: Search + Canvas */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 10 }}>
          <Input.Search placeholder="搜尋模組（formId 或中文名）..." allowClear style={{ width: 280 }}
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)} onSearch={v => setSearchQuery(v)} />
        </div>
        <div style={{ flex: 1 }}>
          <ModuleFlowCanvas nodesData={flowData.nodes} edgesData={flowData.edges}
            searchQuery={searchQuery} domainFilter={domainFilter}
            onNodeSelect={handleNodeSelect} onNodeDoubleClick={handleNodeDoubleClick} />
        </div>
      </div>

      {/* Right: Detail Panel */}
      {selected && (
        <div style={{ width: 320, flexShrink: 0, padding: 16, overflowY: 'auto', background: 'rgba(20,20,40,0.95)', borderLeft: '1px solid #555' }}>
          <Card size="small" title={<span style={{ color: '#fff' }}>{selected.name}</span>}
            extra={<a style={{ color: '#888', cursor: 'pointer' }} onClick={() => setSelected(null)}>X</a>}
            styles={{ header: { background: 'rgba(30,30,60,0.9)', borderBottom: '1px solid #555' }, body: { background: 'transparent' } }}
            style={{ background: 'transparent', border: '1px solid #555' }}>
            <Descriptions column={1} size="small" labelStyle={{ color: '#888' }} contentStyle={{ color: '#e0e0e0' }}>
              <Descriptions.Item label="Form ID">{selected.id}</Descriptions.Item>
              <Descriptions.Item label="Domain">{DOMAIN_LABELS[selected.domain]}</Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={STATUS_TAG[selected.status]?.color}>{STATUS_TAG[selected.status]?.label}</Tag></Descriptions.Item>
              <Descriptions.Item label="Write Tables"><span style={mono}>{selected.writeTables}</span></Descriptions.Item>
              <Descriptions.Item label="Read Tables"><span style={mono}>{selected.readTables}</span></Descriptions.Item>
            </Descriptions>
            {flowData.routeMap[selected.id] && (
              <Button type="primary" size="small" style={{ marginTop: 8 }}
                disabled={selected.status === 'notstarted'}
                onClick={() => handleNodeDoubleClick(selected)}>
                開啟模組
              </Button>
            )}
            <div style={{ marginTop: 12 }}>
              <Text style={{ color: '#888', fontSize: 11 }}>OUTGOING EDGES ({selected.outgoing.length})</Text>
              <EdgeList items={selected.outgoing} arrow="&rarr;" />
            </div>
            <div style={{ marginTop: 12 }}>
              <Text style={{ color: '#888', fontSize: 11 }}>INCOMING EDGES ({selected.incoming.length})</Text>
              <EdgeList items={selected.incoming} arrow="&larr;" />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default ModuleFlowPage;
